import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

/*
 * Motion preference, resolved from two inputs:
 *   1. the user's explicit choice ('system' | 'on' | 'off'), persisted to
 *      localStorage and settable via the in-app Motion control;
 *   2. the OS `prefers-reduced-motion` media query.
 *
 * `animationsEnabled` is the resolved boolean every animated component reads.
 * The provider also stamps `data-motion="on|off"` on <html> so the CSS guard
 * in index.css can freeze pure-CSS animations to match.
 */

const STORAGE_KEY = 'ploom:motion-preference';
const VALID_PREFERENCES = ['system', 'on', 'off'];

const MotionContext = createContext({
  preference: 'system',
  setPreference: () => {},
  animationsEnabled: true,
});

function readStoredPreference() {
  if (typeof window === 'undefined') return 'system';
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return VALID_PREFERENCES.includes(stored) ? stored : 'system';
  } catch {
    return 'system';
  }
}

function getOsReducedMotion() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export const MotionProvider = ({ children }) => {
  const [preference, setPreferenceState] = useState(readStoredPreference);
  const [osReducedMotion, setOsReducedMotion] = useState(getOsReducedMotion);

  // Track OS preference changes live.
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setOsReducedMotion(mediaQuery.matches);
    update();
    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', update);
      return () => mediaQuery.removeEventListener('change', update);
    }
    mediaQuery.addListener(update);
    return () => mediaQuery.removeListener(update);
  }, []);

  const animationsEnabled =
    preference === 'on' ? true : preference === 'off' ? false : !osReducedMotion;

  const setPreference = useCallback((next) => {
    const value = VALID_PREFERENCES.includes(next) ? next : 'system';
    setPreferenceState(value);
    try {
      window.localStorage.setItem(STORAGE_KEY, value);
    } catch {
      /* persistence is best-effort (private mode, disabled storage, etc.) */
    }
  }, []);

  // Reflect the resolved preference on <html> for the CSS animation guard.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.dataset.motion = animationsEnabled ? 'on' : 'off';
  }, [animationsEnabled]);

  const value = useMemo(
    () => ({ preference, setPreference, animationsEnabled }),
    [preference, setPreference, animationsEnabled]
  );

  return <MotionContext.Provider value={value}>{children}</MotionContext.Provider>;
};

MotionProvider.propTypes = {
  children: PropTypes.node,
};

export const useMotion = () => useContext(MotionContext);

export default MotionContext;
